/**
 * Tool → icon mapping, editable by the player in the Tools dashboard.
 *
 * `/sprites/ui/tools/pack_0..9.png` is the pack's Tool_Icons strip. It is
 * ambiguous at 16px AND it is incomplete: there is no hoe and no watering can in
 * it at all. The four tools were therefore pointed at best-guess cells, and the
 * guesses read wrong in game — the hoe slot drew cell 8 (a bellows, reported as
 * "the hoe is a lantern") and the axe slot drew cell 1 (a thin shaft with a grey
 * head, reported as "the axe is an arrow").
 *
 * `pack_10..13` fix that: they are baked from the pack's own per-tool sheets
 * (scripts/build-tool-icons.py) and each one is unmistakably the tool it names.
 * They are the defaults. The strip stays selectable, so a player who prefers one
 * of its icons can still remap — that was always the point of this module, it was
 * just carrying the burden of a missing icon as well.
 *
 * Both the hotbar and the hover cursor read this, so a change is reflected
 * everywhere at once. Persisted to localStorage.
 */
export type ToolId = 'knife' | 'hoe' | 'axe' | 'pickaxe';
export type ToolIcons = Record<ToolId, number>;

export const TOOL_ICON_COUNT = 14;   // pack_0..9 strip + pack_10..13 baked tools
// Bumped from `terraboma:toolicons` when the baked icons landed: a player who had
// opened the Tools dashboard once had the old wrong guesses PERSISTED, so a new
// default alone would not have reached them.
const KEY = 'terraboma:toolicons:v2';
// pack_10 hoe, pack_11 axe, pack_12 pickaxe, pack_13 watering can — baked from the
// per-tool sheets, so each slot draws its own tool. The knife has no dedicated
// sheet and keeps the strip's sword (cell 4), which is what a knife slot means here.
const DEFAULTS: ToolIcons = { knife: 4, axe: 11, pickaxe: 12, hoe: 10 };

export function loadToolIcons(): ToolIcons {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch { return { ...DEFAULTS }; }
}
export function saveToolIcons(v: ToolIcons) {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* private mode */ }
}

/**
 * The watering can (pack_13).
 *
 * Not a ToolId: watering has no hotbar slot — it is what a tap on a dry bed does,
 * with the hoe or with nothing selected. But the hero raises a real watering can
 * for it (ACTIONS.water in map/player.ts composes the can overlay, tool sheet row
 * 9), so the POINTER has to say so too. It showed a hoe over every plot, whatever
 * the plot needed — the owner's report: "when I'm watering the plot, it should
 * show the water can icon".
 *
 * Fixed rather than remappable because there is no hotbar entry to remap it from.
 * It used to point at strip cell 6, which is not a watering can — the strip has
 * none. pack_13 is the real thing, baked from the pack's Iron_WateringCan sheet.
 */
export const WATER_ICON = 13;
export const waterCursorUrl = () => `/sprites/ui/tools/pack_${WATER_ICON}_cursor.png`;

export const packIconUrl = (idx: number) => `/sprites/ui/tools/pack_${idx}.png`;
/** The icon URL currently assigned to a tool (hotbar uses this native-size). */
export const toolIconUrl = (id: ToolId, map = loadToolIcons()) => packIconUrl(map[id]);
/** Larger 2× image for the hover CURSOR (hotbar stays the smaller icon). */
export const toolCursorUrl = (id: ToolId, map = loadToolIcons()) => `/sprites/ui/tools/pack_${map[id]}_cursor.png`;
